import { fetchAuthSession } from '@aws-amplify/core';
import type { CreateTenant, UpdateTenant, PaginatedResponse, PaginationOptions, TenantWithStatus } from '../types';

export interface TenantSearchOptions extends PaginationOptions {}

export interface SubdomainValidationResponse {
    available: boolean;
    message?: string;
}

const API_BASE_PATH = import.meta.env.VITE_API_BASE_URL || '';

export class TenantApiService {
    async getAuthorizationHeader(): Promise<{ Authorization: string }> {
        const authSession = await fetchAuthSession();
        if (!authSession) {
            throw new Error('No authentication session found');
        }

        const token = authSession.tokens?.idToken?.toString();
        if (!token) {
            throw new Error('No authentication token found');
        }
        return { Authorization: `Bearer ${token}` };
    }

    async getTenants(options: TenantSearchOptions = {}): Promise<PaginatedResponse<TenantWithStatus>> {
        const params = new URLSearchParams();

        if (options.limit) {
            params.append('limit', options.limit.toString());
        }
        if (options.nextToken) {
            params.append('nextToken', options.nextToken);
        }

        const queryString = params.toString();
        const url = queryString ? `${API_BASE_PATH}/api/tenants?${queryString}` : `${API_BASE_PATH}/api/tenants`;

        const authorizationHeader = await this.getAuthorizationHeader();
        const response = await fetch(url, {
            method: 'GET',
            headers: { ...authorizationHeader },
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch tenants: ${response.statusText}`);
        }
        return response.json();
    }

    async getTenant(tenantId: string): Promise<TenantWithStatus> {
        if (!tenantId || !tenantId.trim()) {
            throw new Error('Tenant ID is required');
        }

        const authorizationHeader = await this.getAuthorizationHeader();
        const response = await fetch(`${API_BASE_PATH}/api/tenants/${encodeURIComponent(tenantId)}`, {
            method: 'GET',
            headers: { ...authorizationHeader },
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch tenant: ${response.statusText}`);
        }
        return response.json();
    }

    async createTenant(tenantData: CreateTenant): Promise<TenantWithStatus> {
        if (!tenantData) {
            throw new Error('Tenant data is required');
        }

        if (!tenantData.name || !tenantData.name.trim()) {
            throw new Error('Tenant name is required');
        }
        if (!tenantData.subDomain || !tenantData.subDomain.trim()) {
            throw new Error('Tenant subdomain is required');
        }
        if (!tenantData.region || !tenantData.region.trim()) {
            throw new Error('Tenant region is required');
        }
        if (!tenantData.initialUser) {
            throw new Error('Initial user information is required');
        }

        const authorizationHeader = await this.getAuthorizationHeader();
        const response = await fetch(`${API_BASE_PATH}/api/tenants`, {
            method: 'POST',
            headers: {
                ...authorizationHeader,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(tenantData),
        });
        if (!response.ok) {
            throw new Error(`Failed to create tenant: ${response.statusText}`);
        }
        return response.json();
    }

    async updateTenant(tenantId: string, tenantData: UpdateTenant): Promise<TenantWithStatus> {
        if (!tenantId || !tenantId.trim()) {
            throw new Error('Tenant ID is required');
        }
        if (!tenantData) {
            throw new Error('Tenant data is required');
        }

        const authorizationHeader = await this.getAuthorizationHeader();
        const response = await fetch(`${API_BASE_PATH}/api/tenants/${encodeURIComponent(tenantId)}`, {
            method: 'PUT',
            headers: {
                ...authorizationHeader,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(tenantData),
        });
        if (!response.ok) {
            throw new Error(`Failed to update tenant: ${response.statusText}`);
        }
        return response.json();
    }

    async deleteTenant(tenantId: string): Promise<void> {
        if (!tenantId || !tenantId.trim()) {
            throw new Error('Tenant ID is required');
        }

        const authorizationHeader = await this.getAuthorizationHeader();
        const response = await fetch(`${API_BASE_PATH}/api/tenants/${encodeURIComponent(tenantId)}`, {
            method: 'DELETE',
            headers: { ...authorizationHeader },
        });
        if (!response.ok) {
            throw new Error(`Failed to delete tenant: ${response.statusText}`);
        }
    }

    async validateSubdomain(subdomain: string): Promise<SubdomainValidationResponse> {
        if (!subdomain || !subdomain.trim()) {
            throw new Error('Subdomain is required');
        }

        const cleanSubdomain = subdomain.trim().toLowerCase();

        const authorizationHeader = await this.getAuthorizationHeader();
        const response = await fetch(`${API_BASE_PATH}/api/tenants/validate-subdomain?subDomain=${cleanSubdomain}`, {
            method: 'GET',
            headers: { ...authorizationHeader },
        });
        if (!response.ok) {
            throw new Error(`Failed to validate subdomain: ${response.statusText}`);
        }
        return response.json();
    }
}
